import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db, AUTH_EMAIL_SENDER } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { BRAND } from '../config/brand';
import toast from 'react-hot-toast';

const SELLER_ROLES = ['seller', 'admin', 'consignor', 'auctioneer'];

/**
 * Map a reset failure to something a bidder can act on.
 *
 * NOTE the deliberate absence of any "no account found" case. auth/user-not-found is
 * swallowed upstream in AuthContext.resetPassword to match Firebase's email-enumeration
 * protection, so it never reaches here — and nothing below may reveal whether an address
 * is registered. Every message concerns the REQUEST (bad format, rate limit, offline),
 * never the ACCOUNT.
 */
const resetErrorMessage = (code?: string): string => {
  switch (code) {
    case 'auth/invalid-email':
      return "That doesn't look like a valid email address — check it and try again.";
    case 'auth/too-many-requests':
      return 'Too many attempts from this device. Wait a few minutes, then try again.';
    case 'auth/network-request-failed':
      return "Couldn't reach the server. Check your connection and try again.";
    case 'auth/missing-email':
      return 'Enter the email address you registered with.';
    default:
      return 'Something went wrong sending the reset email. Try again in a moment.';
  }
};

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, resetPassword } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ── Forgot-password panel (inline; deliberately no separate route) ────────
  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSending, setResetSending] = useState(false);
  const [resetSentTo, setResetSentTo] = useState<string | null>(null);
  const [resetError, setResetError] = useState<string | null>(null);

  // Post-login return target (e.g. the lot a signed-out user tried to bid on). Only accept
  // internal same-origin paths ("/...") — never an absolute URL — to avoid open-redirects.
  const nextRaw = searchParams.get('next') || '';
  const nextPath = nextRaw.startsWith('/') && !nextRaw.startsWith('//') ? nextRaw : '';

  const openReset = () => {
    setResetEmail(email); // carry over whatever they already typed
    setResetError(null);
    setResetSentTo(null);
    setShowReset(true);
  };

  const closeReset = () => {
    setShowReset(false);
    setResetError(null);
    setResetSentTo(null);
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    const target = resetEmail.trim();
    if (!target) {
      setResetError(resetErrorMessage('auth/missing-email'));
      return;
    }
    setResetSending(true);
    setResetError(null);
    try {
      await resetPassword(target);
      // Identical confirmation for a registered and an unregistered address — by design.
      setResetSentTo(target);
    } catch (err: any) {
      setResetError(resetErrorMessage(err?.code));
      console.error('Password reset failed:', err);
    } finally {
      setResetSending(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const credential = await login(email, password);
      toast.success('Welcome back!');

      // Redirect by role — read the fresh profile so we don't race the context.
      let role: string | undefined;
      try {
        const userDoc = await getDoc(doc(db, 'users', credential.user.uid));
        role = userDoc.exists() ? (userDoc.data().role as string) : undefined;
      } catch {
        role = undefined;
      }

      if (nextPath) {
        navigate(nextPath); // return the user where they came from (e.g. the lot)
      } else if (role && SELLER_ROLES.includes(role)) {
        navigate('/dashboard');
      } else {
        navigate('/browse');
      }
    } catch (error) {
      // One message for both wrong-email and wrong-password — same enumeration reason
      // as the reset panel. The nudge below points a locked-out bidder at the fix.
      toast.error('Invalid email or password');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="card max-w-md w-full">
        <h1 className="text-2xl font-bold mb-2 text-center">
          Sign In
        </h1>
        <p className="text-sm text-gray-600 text-center mb-6">
          Welcome back to {BRAND.displayName}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              placeholder="you@example.com"
              autoComplete="email"
              required
              disabled={loading}
            />
          </div>

          <div>
            <div className="flex items-center justify-between gap-2">
              <label className="label">Password</label>
              {!showReset && (
                <button
                  type="button"
                  onClick={openReset}
                  className="text-sm text-primary-600 hover:text-primary-700 font-semibold mb-1"
                >
                  Forgot password?
                </button>
              )}
            </div>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
              autoComplete="current-password"
              required
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            className="btn-primary w-full"
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        {/* ── Reset panel ───────────────────────────────────────────────────
            Rendered OUTSIDE the sign-in <form>: nesting a form is invalid HTML and
            Enter would submit the wrong one. */}
        {showReset && (
          <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
            {resetSentTo ? (
              <div>
                <p className="text-sm font-semibold text-green-800">✓ Check your email</p>
                <p className="text-sm text-gray-700 mt-1">
                  If an account exists for{' '}
                  <span className="font-semibold break-all">{resetSentTo}</span>, a password
                  reset link is on its way.
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  It comes from{' '}
                  <span className="font-mono break-all">{AUTH_EMAIL_SENDER}</span> and can take a
                  minute — <span className="font-semibold">check your spam folder</span> if you
                  don't see it.
                </p>
                <div className="flex flex-wrap items-center gap-3 mt-3">
                  <button
                    type="button"
                    onClick={closeReset}
                    className="btn-secondary text-sm py-1 px-3"
                  >
                    Back to sign in
                  </button>
                  <button
                    type="button"
                    onClick={() => { setResetSentTo(null); setResetError(null); }}
                    className="text-sm text-gray-500 hover:text-gray-700 underline"
                  >
                    Use a different email
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleReset}>
                <p className="text-sm font-semibold text-gray-800 mb-1">Reset your password</p>
                <p className="text-xs text-gray-600 mb-3">
                  Enter the email you registered with and we'll send a link to set a new one.
                </p>
                <label className="label" htmlFor="reset-email">Email</label>
                <input
                  id="reset-email"
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="input"
                  placeholder="you@example.com"
                  autoComplete="email"
                  disabled={resetSending}
                />
                {resetError && (
                  <p className="text-sm text-red-700 mt-2" role="alert">
                    {resetError}
                  </p>
                )}
                <div className="flex gap-2 mt-3">
                  <button
                    type="submit"
                    className="btn-primary text-sm py-2 px-3 flex-1"
                    disabled={resetSending}
                  >
                    {resetSending ? 'Sending…' : 'Send reset link'}
                  </button>
                  <button
                    type="button"
                    onClick={closeReset}
                    className="btn-secondary text-sm py-2 px-3"
                    disabled={resetSending}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        <p className="text-center mt-4 text-sm text-gray-600">
          Don't have an account?{' '}
          <Link to="/signup" className="text-primary-600 hover:text-primary-700 font-semibold">
            Create bidder account
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
